import { isDrawerPanelView } from '@/shell/drawerPanel';
import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/hooks/useStores';
import { credentialApi, currentCredentialId, type ICredential } from '@/services/CredentialApi';
import { BOTTOM_BAR_ROW, ViewportBottomBar } from '@/components/AppViewport';
import { useFormat } from '@/i18n/useFormat';
import { useT } from '@/i18n/LocaleProvider';
import { registerEnglishArea } from '@/i18n/catalogue';
import { banners } from '@featit/shared/i18n/messages/en/banners';

registerEnglishArea('banners', banners);

/**
 * "Read-only verification session · expires HH:MM" — rendered wherever this component sits, for as
 * long as it is mounted there (I-0326, concept §3.4, AC-K).
 *
 * **No dismiss control, on purpose.** AC-K's own mutation is "make it dismissable, or render it only
 * on the page the session first lands on" — either one leaves a verification session that has
 * navigated away, or been dismissed once, indistinguishable from an ordinary one. `Layout` mounts
 * this once, above the `<Outlet />` every route renders into, so it survives every route change
 * within the authenticated shell without being told to.
 *
 * Reads the credential list rather than trusting only the token: `kind` is on the JWT payload and
 * costs no round trip to decode, but `expiresAt` is not (`credential-claims.ts`'s `IAccessTokenClaims`
 * deliberately carries no timestamp), and `/auth/credentials` already returns it for the credential
 * list screen. One extra request on sign-in, paid once per session rather than per render.
 */
export const VerificationBanner = observer(function VerificationBanner() {
  const t = useT();
  const format = useFormat();
  const { authStore } = useStores();
  const [credential, setCredential] = useState<ICredential | null>(null);

  useEffect(() => {
    if (!authStore.isAuthenticated) {
      setCredential(null);
      return;
    }
    let cancelled = false;
    void credentialApi
      .list()
      .then((list) => {
        if (cancelled) return;
        const id = currentCredentialId();
        const mine = list.find((entry) => entry.id === id) ?? null;
        setCredential(mine?.kind === 'verification' && !mine.revokedAt ? mine : null);
      })
      .catch(() => {
        // A failed read leaves the banner absent rather than wrong. It is not a security boundary —
        // the server refuses every write regardless of whether this component ever rendered — so
        // failing open here costs nothing but a missing label, and failing to a false claim ("read-
        // write") would be the wrong direction to fail in.
        if (!cancelled) setCredential(null);
      });
    return () => {
      cancelled = true;
    };
  }, [authStore.isAuthenticated, authStore.user?.id]);

  if (!credential || isDrawerPanelView()) return null;

  const expiresAt = new Date(credential.expiresAt);
  const expiresLabel = Number.isNaN(expiresAt.getTime())
    ? null
    : format.date(expiresAt, { hour: '2-digit', minute: '2-digit' });

  return (
    <ViewportBottomBar order="verification">
      <div
        role="status"
        aria-live="polite"
        data-testid="verification-banner"
        data-bottom-bar="verification"
        className={`flex ${BOTTOM_BAR_ROW} items-center justify-center gap-2 border-t border-amber-200 bg-amber-50 px-3 py-2 text-center text-[12px] font-medium text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300`}
      >
        <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          {t('banners.session.readOnly')}
          {expiresLabel ? t('banners.session.expires', { time: expiresLabel }) : ''}
          {credential.label ? ` · ${credential.label}` : ''}
        </span>
      </div>
    </ViewportBottomBar>
  );
});
